/* eslint-disable import/prefer-default-export */
import React from "react";
import { useTranslation } from "react-i18next";
import { Row } from "react-table";

// MUI
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import OutlinedInput from "@mui/material/OutlinedInput";

// MUI Icons
import FilterListOffIcon from "@mui/icons-material/FilterListOff"

// Types
import { SelectFilterWrapperFn, PickerValue } from "@/@types/ui/Table";
import { IDataControllerSub } from "@/@types/lib/dataControllerSub";
import { PickerItemValue } from "@/@types/models/model";

type Option = {
  label: string,
  value: PickerItemValue,
  icon?: string,
  color?: string
}

export const SelectColumnFilter: SelectFilterWrapperFn =
  (setFilterLocalStorage, subModelsWithData, fieldItems) =>
    ({ column: { filterValue, setFilter, preFilteredRows, id } }) => {
      const { t } = useTranslation();

      const handleChange = (evt: SelectChangeEvent) => {
        const { value } = evt.target;
        setFilterLocalStorage(id, value, setFilter);
      };

      const options = React.useMemo(() => {
        const opts = new Set<Option>();
        if (fieldItems) {
          fieldItems.labels.filter((label, l) => {
            const found_value = preFilteredRows.find((pfr: Row) => pfr.values[id] === fieldItems.values[l]);
            if (found_value !== undefined) {
              if (fieldItems.icons) {
                opts.add({
                  color: "primary",
                  icon: fieldItems.icons[l],
                  value: fieldItems.values[l],
                  label
                });
              } else {
                opts.add({
                  value: fieldItems.values[l],
                  label
                });
              }
            }
          });
          return Array.from(opts.values());
        }

        if (subModelsWithData) {
          if (subModelsWithData[id]) {
            const dc: IDataControllerSub = subModelsWithData[id];
            dc.records.filter((pi) => {
              const found_value = preFilteredRows.find((pfr: Row) => {
                const pfrValue = pfr.values[id];
                if (Array.isArray(pfrValue)) {
                  return pfrValue.indexOf(pi.value) >= 0;
                }
                return pfrValue === pi.value;
              });

              if (found_value !== undefined) {
                opts.add({
                  value: pi.value,
                  label: pi.label
                });

              }
            })
            return Array.from(opts.values());
          }
        }
        return [];
      }, [id, preFilteredRows, filterValue]);

      const optionsHasIcons = options.find(x => x.icon !== undefined);

      const renderOptionText = ((opt?: Option) => {
        if (!opt) {
          return t("common.all");
        }
        return t(opt.label);
      })

      const getCustomValue = ((selected: string) => {
        const opt = options.find(x => x.value?.toString() === selected);
        return renderOptionText(opt);
      });

      return (
        <Select
          value={filterValue !== undefined ? String(filterValue) : ""}
          renderValue={getCustomValue}
          onChange={handleChange}
          MenuProps={{
            variant: "menu"
          }}
          variant="outlined"
          defaultValue=""
          displayEmpty
          input={<OutlinedInput size="small" margin="dense" />}
        >
          <MenuItem value="" >
            {optionsHasIcons ?
              <ListItemIcon>
                <FilterListOffIcon fontSize="small" />
              </ListItemIcon>
              : null}
            <ListItemText>{t("common.all")}</ListItemText>
          </MenuItem>
          {(options as Array<PickerValue>).map((option, i) => (
            <MenuItem key={i} value={option.value} >
              {option.icon ? (
                <ListItemIcon>
                  <Icon fontSize="small" style={{ color: option.color }}>
                    {option.icon}
                  </Icon>
                </ListItemIcon>
              ) : null}
              <ListItemText>
                {renderOptionText(option)}
              </ListItemText>
            </MenuItem>
          ))}
        </Select>
      );
    };
